import classNames from "classnames";
import styles from "./style.module.scss";
import FrameIcon from "assets/icons/frameIcon";
import ImageIcon from "assets/icons/imageIcon";
import ObjectIcon from "assets/icons/ObjectIcon";
import { truncateFilename } from "helpers/utility/utilities";
import {
  CardData,
  MDOption,
  MetadataType,
  CardEntityTypes,
  CardPreviewData,
  DatasetStatus,
} from "types";
import TagIcon from "assets/icons/tagIcon";
import CaptionIcon from "assets/icons/CaptionIcon";
import UserTagIcon from "assets/icons/UserTagIcon";
import TextWithTooltip from "../../../../../components/TextWithTooltip";

// Helper function to pluralize based on count
const pluralize = (count: number, value: string) =>
  `${value}${count > 1 ? "s" : ""}`;

export function getTitleData(
  singleData: any,
  clusterId: string | undefined,
  datasetId: string | undefined,
  fileName: string,
  isExportSelected: boolean,
  isCardSelected: boolean,
): {
  titleIcon: JSX.Element;
  title: string | JSX.Element;
} {
  // Default values
  let title: string | JSX.Element = fileName;
  let type = CardEntityTypes.Image;
  const status = isCardSelected
    ? "Similarity"
    : isExportSelected
      ? "Export"
      : "Unselected";

  // Determine the title and type based on datasetId, clusterId, and data
  if (datasetId || clusterId) {
    if (clusterId) {
      const data: CardPreviewData = singleData;
      type = data?.video_uri
        ? CardEntityTypes.Frame
        : data?.type.includes("OBJECT")
          ? CardEntityTypes.Object
          : CardEntityTypes.Image;
    } else {
      const data: CardData = singleData;
      const {
        n_objects,
        n_images,
        n_frames,
        n_videos,
        previews,
        type: dataType,
      } = data;

      if (previews.length === 1) {
        // Single preview, determine type based on that
        type = previews[0]?.video_uri
          ? CardEntityTypes.Frame
          : previews[0]?.type === "OBJECT"
            ? CardEntityTypes.Object
            : CardEntityTypes.Image;
      } else {
        // Generate title based on counts
        if (dataType === "OBJECTS") {
          title = `${n_objects} ${pluralize(n_objects, "Object")}${
            n_images > 0 ? ` (${n_images} ${pluralize(n_images, "Image")})` : ""
          }`;
          type = CardEntityTypes.Objects;
        } else {
          if (n_frames > 0) {
            const remainingImages = n_images - n_frames;
            title = `${n_frames} ${pluralize(n_frames, "Frame")}`;
            if (remainingImages > 0) {
              title += ` + ${remainingImages} ${pluralize(n_images, "Image")}`;
              type = CardEntityTypes.Images;
            } else {
              type = CardEntityTypes.Frames;
            }
          } else {
            title = `${n_images} ${pluralize(n_images, "Image")}`;
            type = CardEntityTypes.Images;
          }
        }

        if (n_videos > 0) {
          title += ` (${n_videos} ${pluralize(n_videos, "Video")})`;
        }
      }
    }
  }

  // icon based on the type
  let titleIcon: JSX.Element = <></>;
  switch (type) {
    case CardEntityTypes.Frame:
    case CardEntityTypes.Frames:
      titleIcon = <FrameIcon />;
      break;
    case CardEntityTypes.Image:
    case CardEntityTypes.Images:
      titleIcon = <ImageIcon />;
      break;
    case CardEntityTypes.Object:
    case CardEntityTypes.Objects:
      titleIcon = <ObjectIcon />;
      break;
  }
  // in case title should be a JSX Element
  if ([CardEntityTypes.Frame, CardEntityTypes.Object].includes(type)) {
    title = (
      <div
        className={classNames(styles.fileTitle, {
          [styles.fileTitleWithSimilarity]: status === "Similarity",
          [styles.fileTitleWithExport]: status === "Export",
        })}
      >
        <TextWithTooltip
          fontSize={12}
          className={styles.title}
          text={`${type} from ${truncateFilename(title, 28)}`}
        />
      </div>
    );
  }

  return { title, titleIcon };
}

export function getDebugInfoText(
  clusterID: string,
  clusters: any[],
  showDebugInfo: boolean,
  singleData: any,
  isClusterPage: boolean,
) {
  if (!showDebugInfo) {
    return undefined;
  }

  const selectedCluster = isClusterPage
    ? clusters?.find((cluster: any) => cluster.cluster_id === clusterID)
    : singleData;

  let tooltipText = "";
  let clusterPreview = isClusterPage ? singleData : false;

  if (selectedCluster) {
    if (selectedCluster.formed_by) {
      tooltipText += `formed_by: ${selectedCluster.formed_by}\n`;
    }
    if (
      selectedCluster.relevance_score_type &&
      selectedCluster.relevance_score !== undefined
    ) {
      tooltipText += `${selectedCluster.relevance_score_type}: ${selectedCluster.relevance_score}\n`;
    }
  }
  if (clusterPreview) {
    if (
      clusterPreview.relevance_score_type &&
      clusterPreview.relevance_score !== undefined
    ) {
      tooltipText += `${clusterPreview.relevance_score_type}: ${clusterPreview.relevance_score}\n`;
    }
  }
  return tooltipText;
}

const METADATA_OPTIONS = [
  { icon: <TagIcon />, title: "Labels", type: MetadataType.IMAGE_LABELS },
  { icon: <ObjectIcon />, title: "Labels", type: MetadataType.OBJECT_LABELS },
  {
    icon: <CaptionIcon type="IMAGE" />,
    type: MetadataType.IMAGE_CAPTIONS,
    title: "Captions",
  },
  {
    icon: <CaptionIcon type="OBJECT" />,
    type: MetadataType.OBJECT_CAPTIONS,
    title: "Captions",
  },

  { icon: <UserTagIcon />, title: "User Tags", type: MetadataType.USER_TAGS },
];

export const getMdOptions = (
  isObject: boolean,
  isCaptionsVisible: boolean = true,
): MDOption[] => {
  return METADATA_OPTIONS.filter((option) => {
    if (
      !isCaptionsVisible &&
      [MetadataType.IMAGE_CAPTIONS, MetadataType.OBJECT_CAPTIONS].includes(
        option.type,
      )
    ) {
      return false;
    }
    if (isObject) {
      return ![MetadataType.IMAGE_LABELS, MetadataType.IMAGE_CAPTIONS].includes(
        option.type,
      );
    } else {
      return ![
        MetadataType.OBJECT_LABELS,
        MetadataType.OBJECT_CAPTIONS,
      ].includes(option.type);
    }
  });
};

export const getMDIcon = (mdType: MetadataType) => {
  switch (mdType) {
    case MetadataType.IMAGE_CAPTIONS:
      return <CaptionIcon type={"IMAGE"} />;
    case MetadataType.OBJECT_CAPTIONS:
      return <CaptionIcon type={"OBJECT"} />;
    case MetadataType.USER_TAGS:
      return <UserTagIcon />;
    case MetadataType.IMAGE_LABELS:
      return <TagIcon />;
    case MetadataType.OBJECT_LABELS:
      return <ObjectIcon />;
  }
};

export const getDisabledEnrichmentMessageByDatasetStatus = (
  status: DatasetStatus,
) => {
  return (
    <div className={styles.disabledEnrichment}>
      Enrichment is disabled for {status?.toLowerCase()} datasets.
    </div>
  );
};
